import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { BarStack } from '@visx/shape';
import { Group } from '@visx/group';
import { Grid } from '@visx/grid';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import { useTooltip, useTooltipInPortal, defaultStyles } from '@visx/tooltip';
import { LegendOrdinal } from '@visx/legend';
import { Text } from '@visx/text';
import { useTheme } from '@material-ui/core/styles';
import grey from '@material-ui/core/colors/grey';
import { getContrastTextFromHex } from '../../commons/theme-provider/constants';
import MonthAxis from './MonthAxis';
import YearAxis from './YearAxis';

const defaultMargin = { top: 40, right: 40, bottom: 60, left: 40 };
const tooltipStyles = {
  ...defaultStyles,
  minWidth: 60,
  backgroundColor: 'rgba(0,0,0,0.9)',
  color: 'white',
};

// short month
const formatMonth = (date) =>
  `${new Date(date).toLocaleString('en-us', { month: 'short' })}`;

// accessors
const getDate = (d) => d?.date;

let tooltipTimeout = null;

const StackedDateChart = ({ width, height, bins, margin }) => {
  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    hideTooltip,
    showTooltip,
  } = useTooltip();
  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    scroll: true,
  });

  // Delays keeps tooltip for the duration
  const handleOnMouseLeave = () => {
    tooltipTimeout = window.setTimeout(() => {
      hideTooltip();
    }, 300);
  };

  // Places the tooltip on the bar calculated
  // from absolute position of the chart
  const handleOnMouseMove = (bar) => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);
    const left = bar.x + bar.width / 2;
    showTooltip({
      tooltipData: bar,
      tooltipTop: bar.y,
      tooltipLeft: left,
    });
  };

  // colors
  const theme = useTheme();
  const { primary, secondary, colorant, backdrop } = theme.palette;

  // bounds
  const maxWidth = width;
  const maxHeight = height - margin.top - margin.bottom;
  const dimensions = { height, width, maxHeight, maxWidth, margin };

  // const data = bins.slice(0, bins.length);
  const keys = bins.length && Object.keys(bins[0]).filter((d) => d !== 'date');

  const valueTotals = useMemo(
    () =>
      bins.reduce((allTotals, currentDate) => {
        const total = keys.reduce((dailyTotal, k) => {
          // eslint-disable-next-line no-param-reassign
          dailyTotal += Number(currentDate[k]);
          return dailyTotal;
        }, 0);
        allTotals.push(total);
        return allTotals;
      }, []),
    [bins, keys]
  );

  // scales
  const monthScale = useMemo(
    () =>
      scaleBand({
        domain: bins.map(getDate),
        range: [0, maxWidth],
        padding: 0.2,
      }),
    [bins, maxWidth]
  );

  const yearScale = useMemo(
    () =>
      scaleLinear({
        domain: [0, bins.length],
        range: [0, maxWidth],
      }),
    [bins.length, maxWidth]
  );

  const barScale = useMemo(
    () =>
      scaleLinear({
        domain: [0, Math.max(...valueTotals)],
        range: [maxHeight, 0],
        nice: true,
      }),
    [maxHeight, valueTotals]
  );

  const colorScale = useMemo(
    () =>
      scaleOrdinal({
        domain: keys,
        range: [primary.main, secondary.main, colorant.main],
      }),
    [colorant.main, keys, primary.main, secondary.main]
  );

  return width < 10 ? null : (
    <div style={{ position: 'relative' }}>
      <svg
        ref={containerRef}
        width={width}
        height={height}
        style={{
          borderRadius: '8px',
          boxShadow: '0px 1px gray',
        }}
      >
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          fill={backdrop.main}
          rx={4}
        />
        <Grid
          top={margin.top}
          xScale={monthScale}
          yScale={barScale}
          width={maxWidth}
          height={maxHeight}
          stroke="black"
          strokeOpacity={0.1}
          xOffset={monthScale.bandwidth() / 2}
        />
        <Group top={margin.top}>
          <BarStack
            data={bins}
            keys={keys}
            x={getDate}
            xScale={monthScale}
            yScale={barScale}
            color={colorScale}
          >
            {(barStacks) =>
              barStacks.map((barStack) =>
                barStack.bars.map((bar) => (
                  <svg
                    key={`bar-stack-${barStack.index}-${bar.index}`}
                    x={bar.x}
                    y={bar.y}
                    height={bar.height}
                    width={bar.width}
                  >
                    <rect
                      x="0"
                      y="0"
                      height={bar.height}
                      width={bar.width}
                      fill={bar.color}
                      onMouseLeave={handleOnMouseLeave}
                      onMouseMove={() => handleOnMouseMove(bar)}
                    />
                    {bins[bar.index][bar.key] !== 0 ? (
                      <Text
                        x="50%"
                        y="50%"
                        dominantBaseline="middle"
                        textAnchor="middle"
                        verticalAnchor="middle"
                        fontSize={12}
                        fill={getContrastTextFromHex(bar.color)}
                      >
                        {bins[bar.index][bar.key]}
                      </Text>
                    ) : null}
                  </svg>
                ))
              )
            }
          </BarStack>
        </Group>
        <YearAxis scale={yearScale} dimensions={dimensions} bins={bins} />
        <MonthAxis scale={monthScale} dimensions={dimensions} />
      </svg>
      <div
        style={{
          position: 'absolute',
          top: margin.top / 2 - 10,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          fontSize: '14px',
          color: grey[700],
        }}
      >
        <LegendOrdinal
          scale={colorScale}
          direction="row"
          labelMargin="0 15px 0 0"
        />
      </div>

      {tooltipOpen && tooltipData && (
        <TooltipInPortal
          top={tooltipTop}
          left={tooltipLeft}
          style={tooltipStyles}
        >
          <div style={{ color: colorScale(tooltipData.key) }}>
            <strong>{tooltipData.key}</strong>
          </div>
          <div>{`${tooltipData.bar.data[tooltipData.key]}`}</div>
          <div>
            <small>{formatMonth(getDate(tooltipData.bar.data))}</small>
          </div>
        </TooltipInPortal>
      )}
    </div>
  );
};

StackedDateChart.defaultProps = {
  margin: defaultMargin,
};

StackedDateChart.propTypes = {
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  bins: PropTypes.array.isRequired,
  margin: PropTypes.object,
};

export default StackedDateChart;
